import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import CommonwealthSlide from './CommonwealthSlide';
import { commonwealthSlidePropTypes } from 'utils/proptypes';
import styles from './commonwealthSlider.module.scss';
import { SliderBtn, SliderArrow } from 'svgs';

const CommonwealthSlider = ({ slides }) => {
  const [activeSlide, setActiveSlide] = useState(0);
  const sliderRef = useRef(null);
  //Set a maximum slider width for larger screen widths to keep slider buttons centered
  const normalSlideWidth = 460;
  const activeSlideWidth = 678;
  const sliderPadding = 120;
  const sliderMaxWidth = (slides.length - 1) * normalSlideWidth + activeSlideWidth + sliderPadding;

  const scrollToActiveSlide = index => {
    const activeSlide = sliderRef.current.children[index];
    //Calculate where the center of the active slide is within the slider container
    const centerOfSlide =
      activeSlide.offsetLeft - sliderRef.current.offsetWidth / 2 + activeSlide.offsetWidth / 2;
    sliderRef.current.scrollTo({
      left: centerOfSlide,
      behavior: 'smooth',
    });
  };

  const goToNextSlide = () => {
    if (activeSlide < slides.length - 1) {
      setActiveSlide(prevIndex => prevIndex + 1);
    }
  };

  const goToPreviousSlide = () => {
    if (activeSlide > 0) {
      setActiveSlide(prevIndex => prevIndex - 1);
    }
  };

  const jumpToSlide = currentIndex => {
    setActiveSlide(currentIndex);
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowLeft') {
      goToPreviousSlide();
    } else if (event.key === 'ArrowRight') {
      goToNextSlide();
    }
  };

  useEffect(() => {
    scrollToActiveSlide(activeSlide);
  }, [activeSlide]);

  return (
    <div
      className={styles.cwSliderContainer}
      role="region"
      aria-roledescription="slider"
      style={{ width: '100%', maxWidth: sliderMaxWidth }}>
      <div ref={sliderRef} className={styles.cwSlider}>
        {slides.map((slide, index) => (
          <CommonwealthSlide
            goToPreviousSlide={goToPreviousSlide}
            goToNextSlide={goToNextSlide}
            handleKeyDown={handleKeyDown}
            key={index}
            isActive={activeSlide === index}
            image={slide.image}
            headline={slide.headline}
            copy={slide.copy}
          />
        ))}
      </div>
      <div className={styles.cwSliderBtnContainer}>
        <div
          className={styles.cwSliderArrowPrev}
          role="button"
          tabIndex={0}
          aria-label="Previous Slide"
          onClick={goToPreviousSlide}
          onKeyDown={handleKeyDown}>
          <SliderArrow isActive={activeSlide !== 0} />
        </div>
        {slides.map((_, i) => (
          <div
            aria-label={`Go to Slide ${i + 1}`}
            role="button"
            key={i}
            onClick={() => jumpToSlide(i)}
            onKeyDown={handleKeyDown}
            tabIndex={0}>
            <SliderBtn isActive={activeSlide === i} />
          </div>
        ))}
        <div
          role="button"
          tabIndex={0}
          aria-label="Next Slide"
          onClick={goToNextSlide}
          onKeyDown={handleKeyDown}>
          <SliderArrow isActive={activeSlide !== slides.length - 1} />
        </div>
      </div>
    </div>
  );
};

CommonwealthSlider.propTypes = {
  slides: PropTypes.arrayOf(commonwealthSlidePropTypes).isRequired,
};

export default CommonwealthSlider;
